/**
 * Native form controls, styled to match the vendored Untitled UI inputs.
 *
 * The control-plane forms below are `<form>` submissions that lean on native
 * validation - `required`, `pattern`, `min`/`max` - because the payloads they
 * build are the same ones the API validates server-side, and a mismatch there
 * comes back as an opaque 422. React Aria's `Input`/`Select` do not surrender
 * a `FormData`-readable native control with a `pattern`, so these stay native
 * and borrow the design system's classes rather than its components.
 */

import {
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from '../utils/cx';

const CONTROL =
  'w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-hidden ring-inset placeholder:text-placeholder focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The hint deliberately sits outside the `<label>`. A wrapping label makes its
 * whole text content the control's accessible name, so a hint inside it would
 * rename the field to "Operator note Prose written for the next reader…" for
 * anyone reading by that name — screen readers and tests alike.
 */
function FieldShell({
  label,
  hint,
  tip,
  children,
}: {
  label: string;
  hint?: string;
  tip?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {/* `tip` sits beside the label rather than inside it: a tooltip trigger is
          a button, and a button inside a `<label>` would steal the label's
          click and rename the field for anyone reading by accessible name. */}
      <span className="flex flex-wrap items-baseline gap-1.5">
        <label htmlFor={id} className="text-sm font-medium text-secondary">
          {label}
        </label>
        {tip ? <span className="text-xs text-tertiary">{tip}</span> : null}
      </span>
      {children({ id, describedBy: hintId })}
      {hint ? (
        <span id={hintId} className="text-xs text-tertiary">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function TextField({
  label,
  hint,
  tip,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; tip?: ReactNode }) {
  return (
    <FieldShell label={label} {...(hint ? { hint } : {})} {...(tip ? { tip } : {})}>
      {({ id, describedBy }) => (
        <input
          {...props}
          id={id}
          aria-describedby={describedBy}
          autoComplete={props.autoComplete ?? 'off'}
          className={cx(CONTROL, className)}
        />
      )}
    </FieldShell>
  );
}

export function TextAreaField({
  label,
  hint,
  tip,
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: string;
  tip?: ReactNode;
}) {
  return (
    <FieldShell label={label} {...(hint ? { hint } : {})} {...(tip ? { tip } : {})}>
      {({ id, describedBy }) => (
        <textarea
          {...props}
          id={id}
          aria-describedby={describedBy}
          className={cx(CONTROL, className)}
        />
      )}
    </FieldShell>
  );
}

export function SelectField({
  label,
  hint,
  tip,
  options,
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: string;
  tip?: ReactNode;
  /** A bare string is its own label; the object form is for filters whose
   *  value and wording differ, such as an "All roles" option with no value. */
  options: readonly (string | { value: string; label: string })[];
}) {
  return (
    <FieldShell label={label} {...(hint ? { hint } : {})} {...(tip ? { tip } : {})}>
      {({ id, describedBy }) => (
        <select
          {...props}
          id={id}
          aria-describedby={describedBy}
          className={cx(CONTROL, className)}
        >
          {options.map((option) => {
            const { value, label: text } =
              typeof option === 'string' ? { value: option, label: option } : option;
            return (
              <option key={value} value={value}>
                {text}
              </option>
            );
          })}
        </select>
      )}
    </FieldShell>
  );
}

/**
 * A checkbox that still submits with the form. React Aria's `Checkbox` renders
 * no named native control, so a `FormData` read of it comes back empty — the
 * same reason every other field in this file stays native.
 */
export function CheckboxField({
  label,
  hint,
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: string; hint?: string }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <input
          {...props}
          id={id}
          type="checkbox"
          aria-describedby={hintId}
          className={cx(
            'size-4 shrink-0 cursor-pointer rounded accent-brand-solid outline-brand focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        />
        <label htmlFor={id} className="text-sm font-medium text-secondary">
          {label}
        </label>
      </div>
      {hint ? (
        <span id={hintId} className="text-xs text-tertiary">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** The bordered card every control form sits in, so they all read alike. */
export function FormCard({
  title,
  description,
  children,
  onSubmit,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="flex max-w-2xl flex-col gap-4 rounded-xl border border-secondary bg-primary p-5"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-primary">{title}</h3>
        {description ? <p className="text-sm text-tertiary">{description}</p> : null}
      </div>
      {children}
    </form>
  );
}

/**
 * The one line of feedback a submitted control form leaves behind. It is
 * `aria-live` because the submit button is usually still under the pointer
 * when the answer arrives, so nothing else would announce it.
 */
export function FormResult({ children }: { children?: ReactNode }) {
  return (
    <p aria-live="polite" className="text-sm text-tertiary">
      {children}
    </p>
  );
}

/** Shown in place of any mutation control for a role that cannot mutate. */
export function ReadOnlyNote() {
  return <p className="text-sm text-tertiary">Viewer access is read-only.</p>;
}
